/**
 * useConfirmedAction — gate an async action behind a confirmation dialog.
 *
 * Usage:
 *   const publish = useConfirmedAction(async () => { ... });
 *   // In JSX:
 *   <Button onPress={publish.trigger} isPending={publish.pending} />
 *   <PublishConfirmDialog
 *     isOpen={publish.showConfirm}
 *     onConfirm={publish.confirm}
 *     onCancel={publish.cancel}
 *   />
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface ConfirmedAction {
  /** Call this from the button's onPress — opens the dialog. */
  trigger: () => void;
  /** Call this from the dialog's onConfirm — runs the action. */
  confirm: () => void;
  /** Call this from the dialog's onCancel — closes without acting. */
  cancel: () => void;
  /** True while the async action is in flight. */
  pending: boolean;
  /** True while the confirmation dialog is open. */
  showConfirm: boolean;
}

export function useConfirmedAction(
  action: () => Promise<void>,
  onError?: (err: unknown) => void,
): ConfirmedAction {
  const [showConfirm, setShowConfirm] = useState(false);
  const [pending, setPending] = useState(false);

  // Refs keep confirm() stable while always calling the latest action/onError.
  const actionRef = useRef(action);
  const onErrorRef = useRef(onError);
  useEffect(() => { actionRef.current = action; }, [action]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const trigger = useCallback(() => setShowConfirm(true), []);
  const cancel = useCallback(() => setShowConfirm(false), []);

  const confirm = useCallback(async () => {
    setShowConfirm(false);
    setPending(true);
    try {
      await actionRef.current();
    } catch (err) {
      onErrorRef.current?.(err);
    } finally {
      setPending(false);
    }
  }, []);

  return { trigger, confirm, cancel, pending, showConfirm };
}
